import { useMemo } from "react";
import { useSelector } from "react-redux";
import { findSubModuleByName } from "services/utils/HelperFunction";

// One rule for what a user may do on a screen, read from the provisioning
// payload (`permissions` on the screen's submodule):
//
//   viewer                    -> may open the screen, nothing else
//   editor                    -> may add and update
//   first/second level approver -> may delete (and approve)
//
// Editor and approver are independent: an editor without an approver flag
// cannot delete, and an approver without the editor flag cannot add or
// update. Either one is enough to open the screen, otherwise the actions
// they grant could never be reached.
//
// Some payloads only carry viewer/editor. Where neither approver flag is
// sent at all, delete follows editor, so a screen never loses its delete
// action only because the backend does not send the flag yet.
export const permissionsFromFlags = (permissions) => {
  const flags = permissions || {};
  const canEdit = flags.editor === true;
  const sendsApprover =
    "first_level_approver" in flags || "second_level_approver" in flags;
  const canDelete = sendsApprover
    ? flags.first_level_approver === true ||
      flags.second_level_approver === true
    : canEdit;
  return {
    canView: flags.viewer === true || canEdit || canDelete,
    canEdit,
    canDelete,
  };
};

// Where each screen lives in the provisioning payload. The names must match
// the backend's product_name / module_name / sub_module_name exactly.
export const SCREEN_ACCESS = {
  monitoringDashboard: { product: "Monitoring", module: "Dashboard", subModule: "Dashboard" },
  monitoringEventTrace: { product: "Monitoring", module: "Event Trace", subModule: "Event Trace" },
  monitoringDevices: { product: "Monitoring", module: "Devices", subModule: "Devices" },
  productControl: { product: "System Configuration", module: "System Configuration", subModule: "Products" },
  systemPunchProcessing: { product: "System Configuration", module: "System Punch Processing", subModule: "System Punch Processing" },
  cauverContact: { product: "System Configuration", module: "Cauver Contact", subModule: "Cauver Contact" },
  // Editable home of the organisation identity. The backend has no module for
  // it yet, so it uses Product Control's entry until one is added.
  systemOrganisation: { product: "System Configuration", module: "System Configuration", subModule: "Products" },
  organisationDetails: { product: "Administration", module: "Organisation", subModule: "Organisation Details" },
  organisationContacts: { product: "Administration", module: "Organisation", subModule: "Contacts" },
  organisationDevices: { product: "Administration", module: "Organisation", subModule: "Devices" },
  organisationSettings: { product: "Administration", module: "Organisation", subModule: "Settings" },
  supportContacts: { product: "Administration", module: "Support & Contacts", subModule: "Support & Contacts" },
};

// The screen's own product payload has not reached Redux yet (it still
// holds the previous product's). Nothing is granted and nothing is refused
// until it arrives.
const LOADING_ACCESS = Object.freeze({
  loading: true,
  canView: false,
  canEdit: false,
  canDelete: false,
});

export const resolveScreenPermissions = (userScreenAccess, screen) => {
  // Redux holds one product's payload at a time, and module names repeat
  // across products ("Settings", "Devices"), so another product's payload
  // must never decide this screen.
  if (!screen || userScreenAccess?.product_name !== screen.product) {
    return LOADING_ACCESS;
  }
  // A screen missing from the payload grants nothing, same as every other
  // provisioned screen.
  const subModule = findSubModuleByName(
    userScreenAccess.module_details,
    screen.module,
    screen.subModule,
  );
  return { loading: false, ...permissionsFromFlags(subModule?.permissions) };
};

export const useScreenPermissions = (screen) => {
  const userScreenAccess = useSelector(
    (state) => state.tokenReducer.userScreenAccess,
  );
  return useMemo(
    () => resolveScreenPermissions(userScreenAccess, screen),
    [userScreenAccess, screen],
  );
};
